import {
  AlignCenterVertical,
  AlignEndVertical,
  AlignHorizontalSpaceAround,
  AlignStartVertical,
  CalendarDays,
  CaseLower,
  Columns2,
  CreditCard,
  Heading as HeadingIcon,
  LayoutList,
  Rows2,
  SeparatorHorizontal,
  SquareDashed,
  Type,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@Utils/cn';

/**
 * Presentation metadata the prop schemas cannot carry (schemas are a pure
 * JSON-Schema subset shared with the server): layer icons per block type,
 * icons for known icon-toggle enum values, and thumbnail previews for known
 * visual enums like `cardStyle`.
 */

const BLOCK_ICONS: Record<string, LucideIcon> = {
  region: SquareDashed,
  section: SquareDashed,
  heading: HeadingIcon,
  text: Type,
  smallText: CaseLower,
  card: LayoutList,
  divider: SeparatorHorizontal,
  membershipCard: CreditCard,
  eventsCarousel: CalendarDays,
};

export function getBlockIcon(type: string, size = 16): React.ReactNode {
  const Icon = BLOCK_ICONS[type] ?? SquareDashed;
  return <Icon size={size} />;
}

/** Icons for icon-toggle enum values (orientation + alignment). */
export const ENUM_ICONS: Record<string, React.ReactNode> = {
  horizontal: <Columns2 size={20} />,
  vertical: <Rows2 size={20} />,
  left: <AlignStartVertical size={20} />,
  center: <AlignCenterVertical size={20} />,
  right: <AlignEndVertical size={20} />,
  even: <AlignHorizontalSpaceAround size={20} />,
};

/** Human label for an enum value: `image-top` → `Image Top`, `even` → `Even Space`. */
const ENUM_LABELS: Record<string, string> = {
  'even': 'Even Space',
  '1-column': '1 Column',
  '2-column': '2 Columns',
};

export function formatEnumLabel(value: string): string {
  return (
    ENUM_LABELS[value] ??
    value
      .split(/[-_]/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}

/** Mini layout drawing for one `cardStyle` value, used by the thumbnail picker. */
function CardStyleThumb({ style }: { style: string }) {
  if (style === 'overlay-bottom' || style === 'overlay-top') {
    const top = style === 'overlay-top';
    // Copy overlaid on the image, anchored bottom or top, gradient on the
    // text edge.
    return (
      <div
        className={cn(
          'relative flex h-14 w-full flex-col overflow-hidden rounded-md bg-[#DDCDA6] p-1.5',
          top ? 'justify-start' : 'justify-end',
        )}
      >
        <div
          className={cn(
            'absolute size-2 rounded-full border border-[#F3EDE0]',
            top ? 'right-1 bottom-1' : 'top-1 right-1',
          )}
        />
        <div
          className={cn(
            'absolute inset-x-0 h-2/3',
            top
              ? 'top-0 bg-linear-to-b from-[#B49A63] to-transparent'
              : 'bottom-0 bg-linear-to-t from-[#B49A63] to-transparent',
          )}
        />
        <div className="relative space-y-1">
          <div className="h-1 w-1/2 rounded bg-[#F3EDE0]" />
          <div className="h-1 w-3/4 rounded bg-[#EAE0C8]" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-14 w-full flex-col gap-1.5 rounded-md bg-[#EAE0C8] p-1.5">
      <div className="flex min-h-0 min-w-0 grow-2 items-center justify-center rounded-sm bg-[#DDCDA6]">
        <div className="size-2 rounded-full border border-[#F3EDE0]" />
      </div>
      <div className="flex min-w-0 grow flex-col justify-center gap-1">
        <div className="h-1 w-full rounded bg-[#D8C18B]" />
        <div className="h-1 w-3/4 rounded bg-[#D8C18B]" />
      </div>
    </div>
  );
}

/** Thumbnail previews for known visual enum values, keyed by value. */
export function getEnumThumbnail(value: string): React.ReactNode {
  if (
    value === 'image-top' ||
    value === 'overlay-bottom' ||
    value === 'overlay-top'
  ) {
    return <CardStyleThumb style={value} />;
  }
  return null;
}
